import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { leaveApi } from '../api/leave.js';
import { ApiError } from '../api/client.js';
import { ConfirmDialog } from '../components/ConfirmDialog.jsx';

export default function LeavePage() {
    const queryClient = useQueryClient();

    const balance = useQuery({
        queryKey: ['leave', 'balance'],
        queryFn: () => leaveApi.getBalance(),
    });

    const types = useQuery({
        queryKey: ['leave', 'types'],
        queryFn: () => leaveApi.getTypes(),
        staleTime: Infinity,
    });

    const history = useQuery({
        queryKey: ['leave', 'mine'],
        queryFn: () => leaveApi.listMine(),
    });

    const [showForm, setShowForm] = useState(false);
    const [confirmCancel, setConfirmCancel] = useState(null);

    const refresh = () => {
        queryClient.invalidateQueries({ queryKey: ['leave'] });
    };

    return (
        <div style={{ maxWidth: 960, margin: '0 auto', padding: '24px 16px' }}>
            <div className="flex items-center justify-between" style={{ marginBottom: 14 }}>
                <Link
                    to="/"
                    style={{
                        fontSize: 13,
                        color: 'var(--color-text-tertiary)',
                        textDecoration: 'none',
                    }}
                >
                    ← Back to dashboard
                </Link>
            </div>

            <h1 style={{ fontSize: 22, fontWeight: 500, marginBottom: 18 }}>My leave</h1>

            <BalanceCard data={balance.data} loading={balance.isLoading} />

            <div className="flex items-center justify-between" style={{ marginTop: 22, marginBottom: 12 }}>
                <h2 style={{ fontSize: 14, fontWeight: 500, color: 'var(--color-text-secondary)', margin: 0 }}>
                    Recent requests
                </h2>
                <button onClick={() => setShowForm(true)} className="btn btn-primary">
                    + Request leave
                </button>
            </div>

            <HistoryList
                history={history.data}
                loading={history.isLoading}
                onCancel={(req) => setConfirmCancel(req)}
            />

            {showForm && types.data && (
                <NewRequestForm
                    types={types.data.types}
                    backdateMaxDays={types.data.backdate_max_days}
                    onClose={() => setShowForm(false)}
                    onCreated={() => { setShowForm(false); refresh(); }}
                />
            )}

            <ConfirmDialog
                open={!!confirmCancel}
                title="Cancel this leave request?"
                body={
                    confirmCancel
                        ? `Cancel your ${confirmCancel.leave_type} request for ${formatRange(confirmCancel.start_date, confirmCancel.end_date)}? This cannot be undone.`
                        : ''
                }
                confirmLabel="Cancel request"
                confirmTone="danger"
                onConfirm={async () => {
                    try {
                        await leaveApi.cancel(confirmCancel.id);
                        setConfirmCancel(null);
                        refresh();
                    } catch (err) {
                        setConfirmCancel(null);
                        alert(err.message || 'Could not cancel');
                    }
                }}
                onCancel={() => setConfirmCancel(null)}
            />
        </div>
    );
}

function BalanceCard({ data, loading }) {
    if (loading || !data) {
        return (
            <div className="surface-card" style={{ padding: 20 }}>
                <p style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>Loading balance…</p>
            </div>
        );
    }
    const sp = data.shared_pool;
    const yearLabel = `${formatDate(data.leave_year_start)} – ${formatDate(yearEndOf(data.leave_year_start))}`;
    return (
        <div className="surface-card" style={{ padding: 20 }}>
            <div className="flex items-baseline justify-between" style={{ marginBottom: 12 }}>
                <h2 style={{ fontSize: 14, fontWeight: 500, color: 'var(--color-text-secondary)', margin: 0 }}>
                    Balance
                </h2>
                <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{yearLabel}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3" style={{ gap: 12 }}>
                <BalanceTile
                    label="Shared pool remaining"
                    value={sp.remaining}
                    subline={`${sp.used} used of ${sp.allocated}`}
                    accent
                />
                <BalanceTile
                    label="Wedding (lifetime)"
                    value={data.lifetime.wedding.remaining}
                    subline={`${data.lifetime.wedding.used} of ${data.lifetime.wedding.allocation} used`}
                />
                <BalanceTile
                    label="Umrah (lifetime)"
                    value={data.lifetime.umrah.remaining}
                    subline={`${data.lifetime.umrah.used} of ${data.lifetime.umrah.allocation} used`}
                />
            </div>
            <p
                style={{
                    fontSize: 12,
                    color: 'var(--color-text-tertiary)',
                    marginTop: 12,
                    lineHeight: 1.5,
                }}
            >
                Casual, sick, vacation, and parental leave all draw from the shared pool. Wedding and Umrah have separate lifetime balances. Unpaid leave doesn't deduct from any balance.
            </p>
        </div>
    );
}

function BalanceTile({ label, value, subline, accent }) {
    const style = accent
        ? {
            background: 'rgba(83, 74, 183, 0.08)',
            border: '0.5px solid rgba(83, 74, 183, 0.30)',
        }
        : {
            background: 'var(--color-bg-secondary)',
        };
    return (
        <div style={{ ...style, borderRadius: 'var(--border-radius-md)', padding: 12 }}>
            <p style={{ fontSize: 11, color: 'var(--color-text-secondary)', margin: 0 }}>{label}</p>
            <p
                className="tnum"
                style={{
                    fontSize: 24,
                    fontWeight: 500,
                    margin: 0,
                    marginTop: 2,
                    color: accent ? 'var(--color-status-on-leave)' : 'var(--color-text-primary)',
                }}
            >
                {value}
                <span
                    style={{
                        fontSize: 12,
                        color: 'var(--color-text-tertiary)',
                        marginLeft: 4,
                        fontWeight: 400,
                    }}
                >
                    days
                </span>
            </p>
            <p style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 2 }}>{subline}</p>
        </div>
    );
}

function HistoryList({ history, loading, onCancel }) {
    if (loading || !history) {
        return <p style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>Loading history…</p>;
    }
    if (history.data.length === 0) {
        return (
            <div className="surface-card" style={{ padding: 24, textAlign: 'center' }}>
                <p style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>
                    No leave requests yet.
                </p>
            </div>
        );
    }
    return (
        <div className="surface-card" style={{ overflow: 'hidden' }}>
            <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                <thead style={{ background: 'var(--color-bg-secondary)' }}>
                    <tr>
                        <Th>Type</Th>
                        <Th>Dates</Th>
                        <Th>Days</Th>
                        <Th>Status</Th>
                        <Th align="right"></Th>
                    </tr>
                </thead>
                <tbody>
                    {history.data.map((req, idx) => (
                        <tr
                            key={req.id}
                            style={{
                                borderTop: idx === 0 ? 'none' : '0.5px solid var(--color-border-tertiary)',
                            }}
                        >
                            <td style={{ padding: '10px 14px', textTransform: 'capitalize' }}>
                                {req.leave_type}
                            </td>
                            <td style={{ padding: '10px 14px', color: 'var(--color-text-secondary)' }}>
                                {formatRange(req.start_date, req.end_date)}
                                {req.is_half_day && (
                                    <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginLeft: 6 }}>
                                        ({req.half_day_period})
                                    </span>
                                )}
                            </td>
                            <td className="tnum" style={{ padding: '10px 14px' }}>
                                {req.working_days}
                            </td>
                            <td style={{ padding: '10px 14px' }}>
                                <span className={`pill pill-${req.status}`}>{req.status}</span>
                                {req.review_note && req.status === 'rejected' && (
                                    <span
                                        style={{
                                            fontSize: 11,
                                            color: 'var(--color-text-tertiary)',
                                            marginLeft: 8,
                                            fontStyle: 'italic',
                                        }}
                                    >
                                        “{req.review_note}”
                                    </span>
                                )}
                            </td>
                            <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                                {req.status === 'pending' && (
                                    <button
                                        onClick={() => onCancel(req)}
                                        style={{
                                            fontSize: 11,
                                            color: 'var(--color-danger)',
                                            background: 'transparent',
                                            border: 'none',
                                            textDecoration: 'underline',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        Cancel
                                    </button>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function Th({ children, align }) {
    return (
        <th
            style={{
                textAlign: align || 'left',
                fontSize: 11,
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: 'var(--color-text-tertiary)',
                padding: '10px 14px',
            }}
        >
            {children}
        </th>
    );
}

function NewRequestForm({ types, backdateMaxDays, onClose, onCreated }) {
    const today = new Date().toISOString().slice(0, 10);
    const [form, setForm] = useState({
        leave_type: 'casual',
        start_date: today,
        end_date: today,
        is_half_day: false,
        half_day_period: 'morning',
        reason: '',
    });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const onSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError(null);
        try {
            await leaveApi.submit(form);
            onCreated();
        } catch (err) {
            if (err instanceof ApiError) setError(err.message);
            else setError('Could not submit request.');
        } finally {
            setSubmitting(false);
        }
    };

    const onChange = (k) => (e) => {
        const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        setForm((s) => {
            const next = { ...s, [k]: value };
            if (k === 'is_half_day' && value) next.end_date = next.start_date;
            if (k === 'start_date' && next.is_half_day) next.end_date = value;
            return next;
        });
    };

    const selectedType = types.find((t) => t.value === form.leave_type);
    const poolHint = {
        shared: 'Deducts from your shared 12-day pool',
        lifetime: `Deducts from your lifetime ${form.leave_type} allowance`,
        none: 'Does not deduct from any balance',
    }[selectedType?.pool] || '';

    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.45)',
                zIndex: 50,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 16,
            }}
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <form
                onSubmit={onSubmit}
                className="surface-card flex flex-col"
                style={{
                    padding: 22,
                    width: '100%',
                    maxWidth: 520,
                    maxHeight: '90vh',
                    overflow: 'auto',
                    gap: 12,
                }}
            >
                <h2 style={{ fontSize: 15, fontWeight: 500, marginBottom: 4 }}>Request leave</h2>

                <FormField label="Type">
                    <select value={form.leave_type} onChange={onChange('leave_type')} className="select">
                        {types.map((t) => (
                            <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                    </select>
                    <p style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 4 }}>
                        {poolHint}
                    </p>
                </FormField>

                <div>
                    <label
                        className="flex items-center"
                        style={{ gap: 6, fontSize: 13, color: 'var(--color-text-primary)' }}
                    >
                        <input type="checkbox" checked={form.is_half_day} onChange={onChange('is_half_day')} />
                        Half-day only
                    </label>
                    {form.is_half_day && (
                        <select
                            value={form.half_day_period}
                            onChange={onChange('half_day_period')}
                            className="select"
                            style={{ marginTop: 8 }}
                        >
                            <option value="morning">Morning</option>
                            <option value="afternoon">Afternoon</option>
                        </select>
                    )}
                </div>

                <div className="grid" style={{ gap: 10, gridTemplateColumns: '1fr 1fr' }}>
                    <FormField label="Start date">
                        <input
                            type="date"
                            required
                            value={form.start_date}
                            onChange={onChange('start_date')}
                            className="input"
                        />
                    </FormField>
                    <FormField label="End date">
                        <input
                            type="date"
                            required
                            value={form.end_date}
                            onChange={onChange('end_date')}
                            disabled={form.is_half_day}
                            className="input"
                        />
                    </FormField>
                </div>

                <FormField label="Reason (optional)">
                    <textarea
                        rows={3}
                        value={form.reason}
                        onChange={onChange('reason')}
                        className="textarea"
                        maxLength={1000}
                    />
                </FormField>

                {backdateMaxDays > 0 && (
                    <p
                        style={{
                            fontSize: 11,
                            color: 'var(--color-text-tertiary)',
                            background: 'var(--color-bg-secondary)',
                            padding: 10,
                            borderRadius: 'var(--border-radius-md)',
                            lineHeight: 1.5,
                        }}
                    >
                        Backdating allowed up to {backdateMaxDays} days. Useful for sick leave that you couldn't request in advance.
                    </p>
                )}

                {error && (
                    <div
                        style={{
                            fontSize: 13,
                            color: 'var(--color-danger)',
                            background: 'var(--color-pill-rejected-bg)',
                            border: '0.5px solid var(--color-danger-border)',
                            borderRadius: 'var(--border-radius-md)',
                            padding: '8px 12px',
                        }}
                    >
                        {error}
                    </div>
                )}

                <div
                    className="flex justify-end"
                    style={{
                        gap: 8,
                        paddingTop: 12,
                        marginTop: 4,
                        borderTop: '0.5px solid var(--color-border-tertiary)',
                    }}
                >
                    <button type="button" onClick={onClose} className="btn btn-ghost">Cancel</button>
                    <button type="submit" disabled={submitting} className="btn btn-primary">
                        {submitting ? 'Submitting…' : 'Submit request'}
                    </button>
                </div>
            </form>
        </div>
    );
}

function FormField({ label, children }) {
    return (
        <div>
            <label
                style={{
                    display: 'block',
                    fontSize: 12,
                    fontWeight: 500,
                    color: 'var(--color-text-secondary)',
                    marginBottom: 6,
                }}
            >
                {label}
            </label>
            {children}
        </div>
    );
}

function formatRange(start, end) {
    if (start === end) return formatDate(start);
    return `${formatDate(start)} – ${formatDate(end)}`;
}

function formatDate(d) {
    if (!d) return '';
    return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function yearEndOf(yearStart) {
    if (!yearStart) return '';
    const [y] = yearStart.split('-').map(Number);
    return `${y + 1}-06-30`;
}
